import React, { Component } from "react";
import classnames from "classnames";

import { SortOrder } from "@formant/core/model/SortOrder";
import { Button } from "@formant/ui-kit";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";

import { NoDataMessage } from "../NoDataMessage";
import { Table } from "../Table";
import { DataTableHeader } from "./DataTableHeader";
import { DataTableRow } from "./DataTableRow";
import { IDataTableColumn } from "./IDataTableColumn";
import * as styles from "./index.scss";

export interface IDataTableProps<T> {
    rows: T[];
    columns: IDataTableColumn<T>[];
    loading?: boolean;
    nextLoading?: boolean;
    forceLoaderShow?: boolean;
    showMoreBtn?: boolean;
    noResultsText?: string;
    noResultsClassName?: string;
    loaderTopAligned?: boolean;
    className?: string;
    onRowClicked?: (_: T) => void;
    isRowClickable?: (_: T) => boolean;
    sort?: ISimpleSortState;
    onChangeSort?: (sort: ISimpleSortState) => void;
    rowTestId?: (_: T) => string;
    rowKey?: (item: T, index: number) => string;
    onShowMoreBtn?: () => void;
}

export interface ISimpleSortState {
    columnIndex: number;
    columnName?: string;
    sortOrder: SortOrder;
}

export const defaultSimpleSortState: ISimpleSortState = {
    columnIndex: 0,
    sortOrder: "asc",
};

export class DataTable<T> extends Component<IDataTableProps<T>> {
    public render() {
        const { onClickHeader } = this;
        const {
            rows,
            columns,
            loading,
            nextLoading,
            showMoreBtn,
            noResultsText,
            noResultsClassName,
            loaderTopAligned,
            className,
            forceLoaderShow,
            onRowClicked,
            onShowMoreBtn,
            isRowClickable,
            rowKey = (_, i) => i,
            rowTestId,
            sort,
        } = this.props;

        const noResults = rows.length === 0 && !loading;

        const sortColumn = columns[sort?.columnIndex || 0];
        const sortBy = sortColumn?.sortBy;
        if (sortBy) {
            rows.sort((a, b) => {
                const aValue = sortBy(a) || "";
                const bValue = sortBy(b) || "";
                return aValue.localeCompare(bValue);
            });
            if (sort?.sortOrder === "desc") {
                rows.reverse();
            }
        }

        return (
            <div
                style={{ display: showMoreBtn ? "block" : "flex" }}
                className={classnames(
                    styles["data-table"],
                    loading && styles.loading,
                    className,
                    forceLoaderShow && styles.loadingForce,
                )}
            >
                <LoadingIndicator
                    topAligned={loaderTopAligned}
                    visible={loading || false}
                />
                {!noResults && (
                    <Table>
                        <thead>
                            <tr>
                                {columns.map((column, index) => (
                                    <DataTableHeader
                                        key={index}
                                        context={index}
                                        onClick={
                                            column.sortBy &&
                                            this.props.onChangeSort &&
                                            onClickHeader
                                        }
                                        className={column.className}
                                        sortActive={sort?.columnIndex === index}
                                        sortOrder={
                                            (sort?.columnIndex === index &&
                                                sort.sortOrder) ||
                                            "asc"
                                        }
                                        hideBelow={column.hideBelow}
                                        data-testid={`data-table-header-${column.header}`}
                                    >
                                        {column.header}
                                    </DataTableHeader>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, j) => (
                                <DataTableRow
                                    key={rowKey(row, j)}
                                    index={j}
                                    row={row}
                                    columns={columns}
                                    onClicked={
                                        isRowClickable
                                            ? isRowClickable(row)
                                                ? onRowClicked
                                                : undefined
                                            : onRowClicked
                                    }
                                    testId={rowTestId?.(row) ?? rowKey(row, j)}
                                />
                            ))}
                        </tbody>
                    </Table>
                )}
                {noResults && noResultsText && (
                    <NoDataMessage className={noResultsClassName}>
                        {noResultsText}
                    </NoDataMessage>
                )}
                {showMoreBtn && !loading && (
                    <Button
                        size="lg"
                        disabled={nextLoading}
                        loading={nextLoading}
                        onClick={onShowMoreBtn}
                        style={{ width: "100%", marginTop: "1rem" }}
                    >
                        Load More
                    </Button>
                )}
            </div>
        );
    }

    private onClickHeader = (columnIndex: number) => {
        const { sort, onChangeSort, columns } = this.props;
        const column = columns[columnIndex];

        if (!onChangeSort) {
            return;
        }

        if (sort?.columnIndex !== columnIndex) {
            onChangeSort({
                columnIndex,
                sortOrder: "asc",
                columnName: column.headerName,
            });
        } else {
            onChangeSort({
                columnIndex,
                sortOrder: sort?.sortOrder === "asc" ? "desc" : "asc",
                columnName: column.headerName,
            });
        }
    };
}
